import { predictorService } from "../Services/PredictorListService.ts";

interface PredictorPayload {
  predictor_name: string;
  domain: string;
  problem_type: string;
  dataset_name: string;
  is_incremental: boolean;
  shap_enabled: boolean;
  tune_enabled: boolean;
  zero_fill_enabled: boolean;
  frequency: string;
}

export const handleFetchPredictors = async (skip = 0, limit = 10, page = 1): Promise<any> => {
  try {
    const response = await predictorService.getPredictorData(skip, limit, page);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching predictor list:", error);
    throw new Error("Failed to fetch predictors.");
  }
};

export const handleFetchContribution = async (train_id: number) => {
  try {
    const response = await predictorService.fetchContributingFields(train_id);
    return response; 
  } catch (error: any) {
    throw error; 
  }
};

export const handleGenerateContribution = async (train_id: number) => {
  try {
    const response = await predictorService.generateContributingFields(train_id);
    return response; 
  } catch (error: any) {
    throw error; 
  }
};


export const handleRefreshPredictors = async (skip = 0, limit = 10, page = 1): Promise<any> => {
  try {
    const response = await predictorService.refreshPredictorData(skip, limit, page);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching predictor list:", error);
    throw new Error("Failed to fetch predictors.");
  }
};

export const handleDeletePredictor = async (predictorId: number): Promise<void> => {
  try {
    await predictorService.deletePredictorData(predictorId);
  } catch (error: any) {
    console.error("Error deleting predictor:", error);
    throw new Error(error.response?.data?.detail || "Failed to delete predictor");
  }
};
export const handleUpdatePredictor = async (
  train_id: number,
  preprocess_id: number
): Promise<void> => {
  try {
    await predictorService.updatePredictorData(train_id, preprocess_id);
  } catch (error: any) {
    console.error("Error updating predictor:", error);
    if (error?.response?.data?.detail)
      throw error
    else
      throw new Error("Failed to update predictor");
  }
};

export const handleFetchPredictorHeader = async (id: string): Promise<any> => {
  try {
    const response = await predictorService.getPredictorHeader(id);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching predictor header:", error);
    throw new Error("Failed to fetch predictor header.");
  }
};

export const handleSubmitPrediction = async (
  preprocess_id: number,
  features: Record<string, string>,
  target: string,
  train_id: number
): Promise<any> => {
  try {
    const response = await predictorService.submitPrediction(preprocess_id, features, target, train_id);
    return response.data;
  } catch (error: any) {
    console.error("Prediction failed:", error);
    throw new Error("Prediction Failed!");
  }
};

export const handleTrainPredictor = async (
  dataset_id: number, predictor_name: string, domain: string, problem_type: string, columns: any, is_incremental: boolean = false, shap_enabled: boolean = false, tune_enabled: boolean = false, zero_fill_enabled: boolean = false, frequency?: string
): Promise<any> => {
  try {
    const response = await predictorService.TrainPrediction(dataset_id,
      predictor_name,
      domain,
      problem_type,
      columns,
      is_incremental,
      shap_enabled,
      tune_enabled,
      zero_fill_enabled,
      frequency,
    );
    return response.data;
  } catch (error: any) {
    console.error("Prediction failed:", error);
    throw error;
  }
};

export const handleCreatePredictor = async (
  payload: PredictorPayload
): Promise<any> => {
  try {
    const response = await predictorService.createPredictor(payload);
    localStorage.setItem("predictorResponse", JSON.stringify({ ...response.data, is_incremental: payload?.is_incremental, shap_enabled: payload?.shap_enabled, tune_enabled: payload?.tune_enabled, zero_fill_enabled: payload?.zero_fill_enabled, frequency: payload?.frequency}));

    return response.data;
  } catch (error: any) {
    console.error("Error creating predictor:", error);
    throw new Error(error?.response?.data?.detail);
  }
};

export const handleForecastInference = async (
  train_id: number,
  preprocess_id: number,
  steps: number,
  frequency: string,
  unique_id: string,
  dataset_id: number,
  used_exogenous: boolean
): Promise<any> => {
  try {
    const response = await predictorService.ForecastInference(
      train_id,
      preprocess_id,
      steps,
      frequency,
      unique_id,
      dataset_id,
      used_exogenous
    );
    return response.data;
  } catch (error: any) {
    console.error("Forecast inference failed:", error);
    throw error;
  }
};

export const handleBatchPrediction = async (payload): Promise<any> => {
  try {
    const response = await predictorService.BatchPrediction(payload);
    return response.data;
  } catch (error: any) {
    console.error("Error Creating Batch Prediction:", error);
    throw error;
  }
};

export const GetBatchPredictions = async (train_id): Promise<any> => {
  try {
    const response = await predictorService.GetBatchPredictions(train_id);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching recent Batch Predictions");
    throw error;
  }
}

export const GetBatchPredictionResult = async (prediction_id): Promise<any> => {
  try {
    const response = await predictorService.BatchPredictionResult(prediction_id);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching Batch Prediction Results");
    throw error;
  }
}

export const GetModalSummaryResult = async (train_id): Promise<any> => {
  try {
    const response = await predictorService.ModalSummaryResult(train_id);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching Batch Prediction Results");
    throw error;
  }
}

export const DownloadTemplate = async (train_id): Promise<any> => {
  try {
    const response = await predictorService.DownloadTemplate(parseInt(train_id));
    return response.data;
  } catch (error: any) {
    console.error("Error fetching Training template");
    throw error;
  }
}